import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { BUSINESS } from "../data/content";
import { MaskedLine, Reveal } from "../components/Reveal";

export default function Contact() {
  const { lang, t } = useLanguage();
  const hindi = lang === "hi";

  return (
    <div className="pt-28 sm:pt-36" data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <MaskedLine delay={0.1}>
          <span className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold">{t.contact.kicker}</span>
        </MaskedLine>
        <h1 className={`mt-5 leading-[1.02] text-maroon ${hindi ? "font-hindi text-5xl sm:text-7xl" : "font-display text-6xl sm:text-8xl"}`} data-testid="contact-title">
          <MaskedLine delay={0.25}>{t.contact.title}</MaskedLine>
        </h1>
        <Reveal delay={0.45}>
          <p className={`mt-6 max-w-xl text-base text-ink/60 sm:text-lg ${hindi ? "font-hindi" : ""}`} data-testid="contact-subtitle">
            {t.contact.text}
          </p>
        </Reveal>
      </section>

      <section className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 sm:py-20 lg:grid-cols-12" data-testid="contact-details">
        <div className="space-y-5 lg:col-span-5">
          <Reveal>
            <div className="rounded-2xl border border-gold/30 bg-white p-8">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-maroon/5 text-maroon"><MapPin size={18} /></span>
                <p className={`text-[11px] font-semibold uppercase tracking-[0.3em] text-gold ${hindi ? "font-hindi" : ""}`}>{t.contact.addressLabel}</p>
              </div>
              <p className={`mt-4 text-xl leading-snug text-ink ${hindi ? "font-hindi" : "font-display"}`} data-testid="contact-address">
                {hindi ? BUSINESS.addressHi : BUSINESS.addressEn}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="rounded-2xl border border-gold/30 bg-white p-8">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-maroon/5 text-maroon"><Phone size={18} /></span>
                <p className={`text-[11px] font-semibold uppercase tracking-[0.3em] text-gold ${hindi ? "font-hindi" : ""}`}>{t.contact.phoneLabel}</p>
              </div>
              <div className="mt-4 flex flex-wrap gap-3" data-testid="contact-phones">
                {BUSINESS.phones.map((p, i) => (
                  <a
                    key={p}
                    href={`tel:${p}`}
                    data-testid={`contact-phone-${i}`}
                    className="rounded-full border border-maroon/20 px-4 py-2 text-sm font-semibold text-maroon transition-colors duration-300 hover:bg-maroon hover:text-ivory"
                  >
                    {p}
                  </a>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="rounded-2xl border border-gold/30 bg-white p-8">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-maroon/5 text-maroon"><Mail size={18} /></span>
                <p className={`text-[11px] font-semibold uppercase tracking-[0.3em] text-gold ${hindi ? "font-hindi" : ""}`}>{t.contact.emailLabel}</p>
              </div>
              <a
                href={`mailto:${BUSINESS.email}`}
                data-testid="contact-email"
                className="mt-4 inline-block text-lg font-semibold text-maroon underline decoration-gold decoration-2 underline-offset-4 transition-colors duration-300 hover:text-maroon-dark"
              >
                {BUSINESS.email}
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.28}>
            <div className="flex items-center gap-4 rounded-2xl bg-maroon p-8 text-ivory">
              <Clock size={20} className="shrink-0 text-gold" />
              <div>
                <p className={`text-[11px] font-semibold uppercase tracking-[0.3em] text-gold ${hindi ? "font-hindi" : ""}`}>{t.contact.hoursLabel}</p>
                <p className={`mt-1.5 text-sm text-ivory/85 ${hindi ? "font-hindi" : ""}`} data-testid="contact-hours">{t.contact.hours}</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.34}>
            <a
              href={`tel:${BUSINESS.phones[2]}`}
              data-testid="contact-call-now-button"
              className={`group flex items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-bold tracking-wide text-maroon-dark transition-colors duration-300 hover:bg-gold-soft ${hindi ? "font-hindi" : ""}`}
            >
              <Phone size={16} /> {t.contact.callNow} · {BUSINESS.phones[2]}
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <div className="img-print h-full min-h-[28rem] overflow-hidden rounded-2xl border border-gold/30">
            <iframe
              title="HB Bhavan — Bank Road, Sutapatti, Muzaffarpur"
              src={BUSINESS.mapSrc}
              className="h-full min-h-[28rem] w-full"
              loading="lazy"
              data-testid="contact-map"
            />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
